import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Editor from "@monaco-editor/react";
import defs from "./forms/stepDefinitions.json";

type StepCategory = "processing" | "syndication" | "storage";
type ServiceRecord = Record<string, any>;
type ServicesMap = Record<string, ServiceRecord>;

const categoryOrder: StepCategory[] = ["processing", "syndication", "storage"];

const ServiceDefinitionsManager: React.FC = () => {
  const theme = useTheme();
  const accent = theme.palette.status.accent;
  const isDark = theme.palette.mode === "dark";

  const [services, setServices] = useState<ServicesMap>(
    () => structuredClone((defs as any).services || {})
  );
  const serviceIds = useMemo(() => Object.keys(services).sort(), [services]);
  const [selectedServiceId, setSelectedServiceId] = useState<string>(
    () => serviceIds[0] || ""
  );
  const [editorText, setEditorText] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");

  const [newServiceId, setNewServiceId] = useState("");
  const [newServiceCategory, setNewServiceCategory] = useState<StepCategory>("processing");

  const grouped = useMemo(() => {
    return categoryOrder.map((category) => ({
      category,
      ids: serviceIds.filter((id) => services[id]?.category === category),
    }));
  }, [serviceIds, services]);

  useEffect(() => {
    if (!selectedServiceId && serviceIds.length > 0) {
      setSelectedServiceId(serviceIds[0]);
      return;
    }
    if (selectedServiceId && services[selectedServiceId]) {
      setEditorText(JSON.stringify(services[selectedServiceId], null, 2));
    }
  }, [selectedServiceId, serviceIds, services]);

  const parseRecord = (): ServiceRecord | null => {
    try {
      const parsed = JSON.parse(editorText);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        setError("Record JSON must be an object.");
        return null;
      }
      const serviceCategory = parsed.service_category || parsed.category;
      if (!["processing", "syndication", "storage"].includes(serviceCategory)) {
        setError("Record must include service_category: processing | syndication | storage.");
        return null;
      }

      if (typeof parsed.service_description !== "string" || !parsed.service_description.trim()) {
        setError("Record must include non-empty service_description.");
        return null;
      }

      const serviceFlag = parsed.service_flag;
      const validServiceFlag =
        typeof serviceFlag === "string" ||
        (Array.isArray(serviceFlag) && serviceFlag.every((f: unknown) => typeof f === "string"));
      if (!validServiceFlag) {
        setError("Record must include service_flag as string or string[].");
        return null;
      }

      parsed.service_category = serviceCategory;
      parsed.category = serviceCategory;

      if (!parsed.label) {
        parsed.label = parsed.service_description;
      }

      if (!Array.isArray(parsed.stepFlags)) {
        setError("Record must include stepFlags as an array.");
        return null;
      }
      setError("");
      return parsed;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Invalid JSON");
      return null;
    }
  };

  const handleValidate = () => {
    setSuccess("");
    if (parseRecord()) {
      setSuccess("Record is valid.");
    }
  };

  const handleApply = () => {
    setSuccess("");
    const parsed = parseRecord();
    if (!parsed || !selectedServiceId) return;
    setServices((prev) => ({ ...prev, [selectedServiceId]: parsed }));
    setSuccess(`Updated "${selectedServiceId}".`);
  };

  const handleCreate = () => {
    setSuccess("");
    const id = newServiceId.trim();
    if (!id) {
      setError("New service id is required.");
      return;
    }
    if (services[id]) {
      setError(`Service "${id}" already exists.`);
      return;
    }
    const nextRecord: ServiceRecord = {
      service_description: id,
      service_category: newServiceCategory,
      service_flag: [],
      category: newServiceCategory,
      label: id,
      order: 999,
      renderer: "dynamic",
      stepFlags: [],
      fields: [],
      autoPopulateRules: [],
    };
    setServices((prev) => ({ ...prev, [id]: nextRecord }));
    setSelectedServiceId(id);
    setEditorText(JSON.stringify(nextRecord, null, 2));
    setNewServiceId("");
    setError("");
    setSuccess(`Created "${id}".`);
  };

  const handleDelete = () => {
    if (!selectedServiceId) return;
    if (!window.confirm(`Delete service definition "${selectedServiceId}"?`)) return;
    const next = structuredClone(services);
    delete next[selectedServiceId];
    const nextIds = Object.keys(next).sort();
    setServices(next);
    setSelectedServiceId(nextIds[0] || "");
    setEditorText(nextIds[0] ? JSON.stringify(next[nextIds[0]], null, 2) : "");
    setError("");
    setSuccess("Service definition deleted.");
  };

  const handleReset = () => {
    if (!selectedServiceId || !services[selectedServiceId]) return;
    setEditorText(JSON.stringify(services[selectedServiceId], null, 2));
    setError("");
    setSuccess("Editor reset.");
  };

  const handleCopyAll = async () => {
    const payload = JSON.stringify({ services }, null, 2);
    try {
      await navigator.clipboard.writeText(payload);
      setError("");
      setSuccess("Copied full service definitions JSON.");
    } catch {
      setError("Could not copy to clipboard.");
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Service Definitions
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        CRUD editor for service definition records. This is a UI prototype using local state; wire save/load to Dynamo API next.
      </Typography>

      <Paper
        sx={{
          p: 2,
          border: `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.18 : 0.12)}`,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
          <TextField
            size="small"
            label="New Service ID"
            value={newServiceId}
            onChange={(e) => setNewServiceId(e.target.value)}
          />
          <TextField
            size="small"
            select
            label="Category"
            value={newServiceCategory}
            onChange={(e) => setNewServiceCategory(e.target.value as StepCategory)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="processing">processing</MenuItem>
            <MenuItem value="syndication">syndication</MenuItem>
            <MenuItem value="storage">storage</MenuItem>
          </TextField>
          <Button variant="contained" onClick={handleCreate} sx={{ backgroundColor: accent, color: "#fff", "&:hover": { backgroundColor: accent, opacity: 0.92 } }}>
            Create
          </Button>
          <Button variant="outlined" onClick={handleDelete} disabled={!selectedServiceId}>
            Delete
          </Button>
          <Button variant="outlined" onClick={handleValidate} disabled={!selectedServiceId}>
            Validate
          </Button>
          <Button variant="outlined" onClick={handleApply} disabled={!selectedServiceId}>
            Apply Update
          </Button>
          <Button variant="outlined" onClick={handleReset} disabled={!selectedServiceId}>
            Reset Editor
          </Button>
          <Button variant="outlined" onClick={handleCopyAll}>
            Copy Full JSON
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ maxHeight: "70vh", overflow: "auto" }}>
              {grouped.map((group) => (
                <Box key={group.category}>
                  <Typography sx={{ px: 2, py: 1, fontWeight: 700, color: accent }}>
                    {group.category}
                  </Typography>
                  <Divider />
                  <List dense disablePadding>
                    {group.ids.map((id) => (
                      <ListItemButton
                        key={id}
                        selected={id === selectedServiceId}
                        onClick={() => {
                          setSelectedServiceId(id);
                          setError("");
                          setSuccess("");
                        }}
                      >
                        <ListItemText primary={id} />
                      </ListItemButton>
                    ))}
                  </List>
                </Box>
              ))}
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Typography sx={{ fontWeight: 600 }}>
                  {selectedServiceId || "No service selected"}
                </Typography>
              </Box>
              <Editor
                height="70vh"
                defaultLanguage="json"
                language="json"
                theme={isDark ? "vs-dark" : "vs"}
                value={editorText}
                onChange={(value) => setEditorText(value ?? "")}
                options={{
                  minimap: { enabled: false },
                  folding: true,
                  lineNumbers: "on",
                  wordWrap: "on",
                  tabSize: 2,
                  insertSpaces: true,
                  automaticLayout: true,
                }}
              />
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default ServiceDefinitionsManager;
